import * as React from "react";
import { Check, ChevronsUpDown, Loader2, Plus } from "lucide-react";
import { cn } from "@repo/utils";
import {
  Button,
  useToast,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@repo/ui";
import { logger } from "@/lib/utils/logger";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3005";

export interface InventoryItem {
  id: string;
  ndc: string;
  itemName: string;
  drugType: string;
  label: string;
}

export interface InventorySearchComboboxProps {
  value?: string;
  /** When set (e.g. from prefilled data), used for trigger display so NDC shows on second line like search selection. */
  valueItem?: InventoryItem | null;
  onSelect?: (item: InventoryItem | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  displayLabel?: string;
  /** Callback when user wants to create a new product. Receives the current search query. */
  onCreateNew?: (searchQuery: string) => void;
  /** Automatically open the search popover on mount (e.g. when no item is pre-selected). */
  autoOpen?: boolean;
  /**
   * Render the selected state compactly with strict single-line truncation
   * (name + NDC/schedule subtitle) instead of wrapping across multiple lines.
   * Use this when the combobox lives in a narrow container (e.g. a side card).
   */
  compact?: boolean;
  "data-testid"?: string;
}

// Helper to format schedule
function formatSchedule(schedule: string): string {
  if (!schedule) return "";

  const scheduleMap: Record<string, string> = {
    schedule_i: "Schedule I",
    schedule_ii: "Schedule II",
    schedule_iii: "Schedule III",
    schedule_iv: "Schedule IV",
    schedule_v: "Schedule V",
  };

  return (
    scheduleMap[schedule.toLowerCase()] ||
    schedule.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export function InventorySearchCombobox({
  valueItem,
  onSelect,
  placeholder = "Search inventory...",
  searchPlaceholder = "Search by name or NDC...",
  disabled = false,
  className,
  displayLabel,
  onCreateNew,
  autoOpen = false,
  compact = false,
  "data-testid": dataTestId,
}: InventorySearchComboboxProps) {
  const { toast } = useToast();
  const [open, setOpen] = React.useState(false);

  // Auto-open the popover on mount when no item is pre-selected,
  // giving the panel animation time to settle first.
  React.useEffect(() => {
    if (!autoOpen || valueItem) return;
    const timer = setTimeout(() => setOpen(true), 200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [items, setItems] = React.useState<InventoryItem[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState<InventoryItem | null>(
    null
  );

  // When parent provides valueItem (e.g. prefilled from inventory row), sync for display and dropdown state
  React.useEffect(() => {
    if (valueItem !== undefined) {
      setSelectedItem(valueItem ?? null);
    }
  }, [valueItem]);

  // Debounce search
  React.useEffect(() => {
    const abortController = new AbortController();

    const searchInventory = async () => {
      if (searchQuery.length < 1) {
        setItems([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await fetch(
          `${API_URL}/inventory/search?q=${encodeURIComponent(searchQuery)}`,
          { credentials: "include", signal: abortController.signal }
        );

        if (!response.ok) {
          throw new Error("Failed to search inventory");
        }

        const data = await response.json();
        setItems(data.results || []);
      } catch (error) {
        if ((error as Error).name === "AbortError") return;
        logger.error("Inventory search failed", error, {
          operation: "inventory_search",
        });
        toast({
          title: "Search Error",
          description: "Failed to search inventory. Please try again.",
          variant: "destructive",
        });
        setItems([]);
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    const debounceTimer = setTimeout(searchInventory, 300);

    return () => {
      clearTimeout(debounceTimer);
      abortController.abort();
    };
  }, [searchQuery, toast]);

  const handleSelect = (item: InventoryItem) => {
    setSelectedItem(item);
    onSelect?.(item);
    setSearchQuery(""); // Clear search after selection
    setOpen(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      // Clear search when closing if no item was selected
      setSearchQuery("");
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "group w-full justify-between text-left font-normal",
            !className?.includes("h-") &&
              (compact ? "h-auto py-2" : "h-auto min-h-[44px] py-3"),
            className
          )}
          disabled={disabled}
          data-testid={dataTestId}
        >
          {(valueItem ?? selectedItem) ? (
            <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left">
              <span
                className={cn(
                  "w-full font-medium leading-snug",
                  compact ? "truncate" : "whitespace-normal break-words"
                )}
                title={
                  compact ? (valueItem ?? selectedItem)!.itemName : undefined
                }
              >
                {(valueItem ?? selectedItem)!.itemName}
              </span>
              <span
                className={cn(
                  "text-xs text-muted-foreground group-hover:opacity-75 group-hover:[color:inherit] group-focus:[color:inherit]",
                  compact && "w-full truncate"
                )}
              >
                NDC: {(valueItem ?? selectedItem)!.ndc} ·{" "}
                {formatSchedule((valueItem ?? selectedItem)!.drugType)}
              </span>
            </div>
          ) : (
            <span
              className={cn(
                "leading-tight",
                compact ? "truncate" : "whitespace-normal break-words"
              )}
            >
              {displayLabel || placeholder}
            </span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[calc(100vw-2rem)] min-w-[300px] max-w-[500px] p-0 md:w-[500px]"
        align="start"
      >
        <div className="flex flex-col">
          {/* Search Input */}
          <div className="flex items-center border-b border-border px-3">
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
              autoFocus
            />
          </div>

          {/* Results List */}
          <div className="max-h-[300px] overflow-y-auto overflow-x-hidden">
            {isLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="ml-2 text-sm text-muted-foreground">
                  Searching...
                </span>
              </div>
            )}

            {!isLoading && searchQuery.length > 0 && items.length === 0 && (
              <div className="py-4 text-center">
                <p className="text-sm text-muted-foreground mb-3">
                  No inventory items found
                </p>
                {onCreateNew && (
                  <button
                    type="button"
                    onClick={() => {
                      const query = searchQuery;
                      setSearchQuery("");
                      setOpen(false);
                      onCreateNew(query);
                    }}
                    className="inline-flex items-center gap-2 rounded-lg border border-dashed border-primary/40 bg-primary/5 px-4 py-2.5 text-sm font-medium text-primary transition-colors hover:bg-primary/10 hover:border-primary/60"
                  >
                    <Plus className="h-4 w-4" />
                    Add New Product
                  </button>
                )}
              </div>
            )}

            {!isLoading && items.length > 0 && (
              <div className="p-1">
                {items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleSelect(item)}
                    className={cn(
                      "group relative flex w-full cursor-pointer select-none items-start rounded-sm px-2 py-2 text-left text-sm outline-none transition-colors hover:bg-primary-soft hover:text-primary focus:bg-primary-soft focus:text-primary",
                      (valueItem ?? selectedItem)?.id === item.id &&
                        "bg-primary-soft text-primary"
                    )}
                  >
                    <Check
                      className={cn(
                        "mr-2 mt-1 h-4 w-4 shrink-0",
                        (valueItem ?? selectedItem)?.id === item.id
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                    <div className="flex flex-col items-start gap-0.5 min-w-0 flex-1">
                      <TooltipProvider delayDuration={300}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="font-medium leading-snug truncate w-full block">
                              {item.itemName}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{item.itemName}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <span className="text-xs text-muted-foreground group-hover:text-primary/70 group-focus:text-primary/70">
                        NDC: {item.ndc} | {formatSchedule(item.drugType)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
